import { Text } from "@-/dev.improve.haniverse.components.ui.text";
import { Head } from "@-/com.npmjs.expo-router/head";
import { useIsFocused } from "@-/com.npmjs.expo-router/react-navigation";
import {
  PrimaryControlsStripTitleContext,
  type PrimaryControlsStripTitleRegistry,
} from "@-/dev.improve.haniverse.contexts.primary-controls-strip.title";
import { useContext, useEffect, useSyncExternalStore, type ReactNode } from "react";
import { PrimaryControlsStripTitlePublishingContext } from "#primary-controls-strip-title-publishing-context";
import type { PrimaryControlsStripTitleHostGate } from "#use-primary-controls-strip-title-host-gate";
import { Platform, View, type TextStyle } from "@-/com.npmjs.react-native";

const isWeb = Platform.OS === "web";

// `textAlignVertical` is a TextStyle property (no Uniwind utility for it), so the
// plain title's vertical centering goes through `style`. Module-level so the object
// identity is stable across renders.
const PLAIN_TITLE_STYLE: TextStyle = { textAlignVertical: "center" };

function isRenderableAsPlainTitle(value: ReactNode): value is string | number {
  return typeof value === "string" || typeof value === "number";
}

export type PrimaryControlsStripTitleLayout = "default" | "sidebar";
export type PrimaryControlsStripTitleAlignment = "center" | "start";
export type PrimaryControlsStripTitlePresentation = "heading" | "breadcrumb";

function getPlainTitleClassName(
  layout: PrimaryControlsStripTitleLayout,
  align: PrimaryControlsStripTitleAlignment,
  presentation: PrimaryControlsStripTitlePresentation,
) {
  if (presentation === "breadcrumb") {
    return "max-w-full text-left text-base font-medium leading-tight text-muted-foreground";
  }
  // `leading-tight` (1.25×) keeps the line box close to the (possibly shrunk) font size so
  // the wrapping centered container (`items-center justify-center`) vertically centers the
  // title at any `adjustsFontSizeToFit` scale — the default `leading-normal` line box stays
  // fixed while glyphs shrink, which top-aligns shrunken long titles and reads as un-centered.
  // A full `leading-none` (1×) line box is too tight for the ascent+descent, so titles with a
  // cap AND a descender (e.g. "Sign in") clip top and bottom; 1.25× gives the glyph its room
  // back while staying near enough to the font size to keep shrunken titles centered.
  if (layout === "sidebar") {
    return align === "start"
      ? "max-w-full text-left text-base font-bold leading-tight text-foreground"
      : "max-w-full text-center text-base font-bold leading-tight text-foreground";
  }
  return align === "start"
    ? "max-w-full text-left text-xl font-bold leading-tight text-foreground"
    : "max-w-full text-center text-xl font-bold leading-tight text-foreground";
}

function renderHeaderTitleContent(
  currentTitle: NonNullable<ReactNode>,
  layout: PrimaryControlsStripTitleLayout,
  align: PrimaryControlsStripTitleAlignment,
  presentation: PrimaryControlsStripTitlePresentation,
): ReactNode {
  return isRenderableAsPlainTitle(currentTitle) ? (
    <Text
      adjustsFontSizeToFit
      className={getPlainTitleClassName(layout, align, presentation)}
      minimumFontScale={0.6}
      numberOfLines={1}
      // Vertically center the (possibly shrunk) glyphs within the line box on
      // Android; iOS relies on the `leading-none` font-sized box + centered
      // container instead (it ignores `textAlignVertical`). `textAlignVertical` is a
      // TextStyle prop (no Uniwind utility), so it goes through `style` here — the
      // same pattern select.tsx / floating-tab-bar.tsx use for centered glyphs.
      style={PLAIN_TITLE_STYLE}
    >
      {currentTitle}
    </Text>
  ) : (
    <View
      className={
        align === "start"
          ? "h-full max-w-full items-start justify-center"
          : "h-full max-w-full items-center justify-center"
      }
    >
      {currentTitle}
    </View>
  );
}

/** The published title. The gate proves one exists; both read the same store, so they agree. */
function usePublishedPrimaryControlsStripTitle(
  registry: PrimaryControlsStripTitleRegistry,
): NonNullable<ReactNode> {
  const title = useSyncExternalStore(registry.subscribe, registry.getCurrent, registry.getCurrent);
  if (title == null) {
    throw new Error(
      "PrimaryControlsStripTitleHost rendered with no published title; render it only under usePrimaryControlsStripTitleHostGate()",
    );
  }
  return title;
}

/** Place once in `PrimaryControlsStrip` where the centered title should appear, under its gate. */
export function PrimaryControlsStripTitleHost({
  align = "center",
  gate: { registry },
  layout = "default",
  prefix,
  presentation = "heading",
}: {
  align?: PrimaryControlsStripTitleAlignment;
  gate: PrimaryControlsStripTitleHostGate;
  layout?: PrimaryControlsStripTitleLayout;
  prefix?: ReactNode;
  presentation?: PrimaryControlsStripTitlePresentation;
}) {
  const currentTitle = usePublishedPrimaryControlsStripTitle(registry);

  if (presentation === "breadcrumb") {
    return (
      <View className="h-full min-w-0 shrink flex-row items-center">
        {prefix == null ? null : <View className="shrink-0">{prefix}</View>}
        <View className="h-full min-w-0 shrink items-start justify-center">
          {renderHeaderTitleContent(currentTitle, layout, align, presentation)}
        </View>
      </View>
    );
  }

  return (
    <View className="h-full min-w-0 flex-1 overflow-visible px-2">
      <View
        className={
          align === "start"
            ? "size-full min-w-0 flex-1 items-start justify-center"
            : "size-full min-w-0 flex-1 items-center justify-center"
        }
      >
        {renderHeaderTitleContent(currentTitle, layout, align, presentation)}
      </View>
    </View>
  );
}

/**
 * Publishes the screen's title to the registry backing `PrimaryControlsStripTitleProvider`. When multiple
 * callsites are mounted simultaneously, only the most recently registered one is rendered by
 * `PrimaryControlsStripTitleHost`.
 * Unmounting/blurring the screen removes the entry without leaking state.
 *
 * On web, plain `string`/`number` children also become `"<title> — Han"` via
 * `expo-router/head` (picked up by server rendering); non-text children only render in the
 * header host.
 */
export function PrimaryControlsStripTitle({ children }: { children: ReactNode }) {
  const isFocused = useIsFocused();
  const isPublishing = useContext(PrimaryControlsStripTitlePublishingContext);
  const registry = useContext(PrimaryControlsStripTitleContext);
  const shouldPublish = isFocused && isPublishing;

  useEffect(() => {
    if (!shouldPublish) {
      return;
    }
    return registry.publish(children);
  }, [children, registry, shouldPublish]);

  // A publisher: on web a plain-text title also becomes the document title.
  return (
    <>
      {isWeb && shouldPublish && isRenderableAsPlainTitle(children) && (
        <Head>
          <title>{`${children} — Han`}</title>
        </Head>
      )}
    </>
  );
}
